import { create } from 'zustand';
/**
 * Authentication store using Zustand.
 * Stores user profile and tokens IN MEMORY ONLY (no persistence).
 *
 * Security principles:
 * - Access tokens never saved to localStorage (XSS risk)
 * - Tokens cleared on logout or page refresh (requires re-login)
 * - User must re-authenticate after browser close
 */
export const useAuthStore = create((set) => ({
    // Initial state
    user: null,
    tokens: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
    // Set user and mark as authenticated
    setUser: (user) => set({
        user,
        isAuthenticated: true,
        error: null,
    }),
    // Set tokens (in memory only)
    setTokens: (tokens) => set({
        tokens,
    }),
    // Set loading state
    setLoading: (loading) => set({
        isLoading: loading,
    }),
    // Set error state
    setError: (error) => set({
        error,
        isLoading: false,
    }),
    // Logout: clear all auth state
    logout: () => {
        // Clear selected tenant from localStorage
        localStorage.removeItem('selectedTenantId');
        set({
            user: null,
            tokens: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
        });
    },
    // Reset: clear all state (used on errors)
    reset: () => {
        set({
            user: null,
            tokens: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
        });
    },
}));
/**
 * Selectors for common auth state queries.
 * Use these to avoid unnecessary re-renders.
 */
export const selectUser = (state) => state.user;
export const selectIsAuthenticated = (state) => state.isAuthenticated;
export const selectIsLoading = (state) => state.isLoading;
export const selectError = (state) => state.error;
export const selectTokens = (state) => state.tokens;
export const selectHasRole = (role) => (state) => state.user?.roles.includes(role) ?? false;
export const selectIsAdmin = (state) => state.user?.roles.includes('admin') ?? false;
export const selectIsManager = (state) => (state.user?.roles.includes('manager') || state.user?.roles.includes('admin')) ?? false;
